import { Fragment, type CSSProperties } from "react";
import { splitHits } from "@/lib/searchHits";
import { testIds } from "@/testIds";

/**
 * A search hit: the matched characters, on the warm fill (Olivia,
 * REPLY-2026-10-06b §1). The fill is never text, so the ink is always
 * -warm-ink. On a selected row (the cerulean wash) it stays warm: the two
 * hues are complements, so the hit still reads.
 *
 * Built to be reused by Find in document when it arrives: every hit is this
 * mark, and the current one adds a 2 px -warm-ink outline (`current`), so it
 * isn't told apart by colour alone.
 */
const MARK: CSSProperties = {
  background: "var(--lilia-accent-warm)",
  color: "var(--lilia-accent-warm-ink)",
  borderRadius: 3,
  padding: "0 2px",
  boxDecorationBreak: "clone",
  WebkitBoxDecorationBreak: "clone",
};

const CURRENT: CSSProperties = { outline: "2px solid var(--lilia-accent-warm-ink)" };

export function SearchHitMark({ children, current = false }: { children: string; current?: boolean }) {
  return (
    <mark data-testid={testIds.warm.searchHitMark} data-current={current || undefined} style={current ? { ...MARK, ...CURRENT } : MARK}>
      {children}
    </mark>
  );
}

/** `text`, with what `query` matched in it marked. */
export function SearchHitText({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitHits(text, query).map((run, i) =>
        run.hit ? <SearchHitMark key={i}>{run.text}</SearchHitMark> : <Fragment key={i}>{run.text}</Fragment>,
      )}
    </>
  );
}
